import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Measure from "react-measure";
import { useDispatch } from "react-redux";
import { getAbsoluteThermalImage } from "redux/actions";
import { fabric } from 'fabric';
import OBCSpinner from "components/util/OBC";
import _ from "lodash";

const AbsoluteColourImageDisplay = ({
    sessionKey,
    videoKey,
    deviceKey,
    frame,
    displayRawImage,
    thermalImageSrc,
    setLoading,
    absoluteImageLoading
}) => {

    const dispatch = useDispatch();
    const [image, setImage] = useState(null);
    const [dimensions, setDimensions] = useState({});
    const [fabricCanvasRef, setFabricCanvasRef] = useState(null);
    const [fabricCanvas, setFabricCanvas] = useState(null);
    const [absoluteImageCanvas, setAbsoluteImageCanvas] = useState(null);
    const [imageObj, setImgObj] = useState(null);
    const [hoveredTemp, setHoveredTemp] = useState(null);
    const [canvasDimensions, setCanvasDimensions] = useState([100, 100]);
    const thermalImage = useRef(null);

    const setZoomPreview = useCallback((rawMouseX, rawMouseY) => {
        if(imageObj){
            let clonedImage = fabric.util.object.clone(imageObj);
            if(thermalImage.current){
                clonedImage = thermalImage.current;
            }
            const CROP_SIZE = 10;
            const CROPPED_IMAGE_SCALE = 5;
            
            const originalSize = clonedImage.getOriginalSize();
            
            const imageWidthScale = fabricCanvas.width / originalSize.width;
            const imageHeightScale = fabricCanvas.height / originalSize.height;

            const scaledMouseX = rawMouseX / imageWidthScale;
            const scaledMouseY = rawMouseY / imageHeightScale;

            const cropLeft = scaledMouseX - (CROP_SIZE/2);
            const cropTop = scaledMouseY - (CROP_SIZE/2);

            const cropData = {
                cropX: cropLeft,
                cropY: cropTop,
                width: CROP_SIZE,
                height: CROP_SIZE,
                left: rawMouseX - ((CROP_SIZE*CROPPED_IMAGE_SCALE)/2),
                top: rawMouseY - ((CROP_SIZE*CROPPED_IMAGE_SCALE)/2),
                scaleX: CROPPED_IMAGE_SCALE,
                scaleY: CROPPED_IMAGE_SCALE,
                hoverCursor: "crosshair",
            }

            clonedImage.set(cropData)
            thermalImage.current = clonedImage;
            fabricCanvas.add(clonedImage);
            fabricCanvas.renderAll();
        }
    }, [fabricCanvas, imageObj])
    
    const onMove = useCallback((evt, canvas) => {
        const ctx = absoluteImageCanvas.getContext("2d");
        const x = parseInt(evt.pointer.x);
        const y = parseInt(evt.pointer.y);

        const px = ctx.getImageData(x, y, 1, 1).data;
        const temp = Math.floor((px[0] * 0.64) - 35);
        setHoveredTemp(temp);
        setZoomPreview(x, y)
    }, [absoluteImageCanvas, setZoomPreview]);

    useEffect(() => {
        if(fabricCanvas){
            fabricCanvas.on("mouse:move", function(evt){onMove(evt, this)});

            return () => {
                fabricCanvas.__eventListeners["mouse:move"] = [];
            }
        }
    }, [fabricCanvas, onMove]);


    useEffect(() => {
        if(fabricCanvasRef){
            const canvas = new fabric.Canvas(fabricCanvasRef, {
                uniformScaling: false,
                defaultCursor: "crosshair",
                selection: false,
                containerClass: "AbsoluteFabricCanvasContainer"
            });
            setFabricCanvas(canvas);
        }
    }, [fabricCanvasRef]);

    useEffect(() => {
        if(sessionKey && deviceKey && videoKey){
            setLoading(true)
            dispatch(getAbsoluteThermalImage(deviceKey, sessionKey, videoKey, frame))
                .then(absoluteImage => {
                    if(absoluteImage.image){
                        if(absoluteImage.image === image){
                            setLoading(false);
                        }
                        setImage(absoluteImage.image);
                    }else{
                        setImage(null);
                    }
                })
                .catch(error => {
                    console.log("debug error getting absolute temp image", error)
                })
        }
    }, [deviceKey, dispatch, frame, sessionKey, videoKey])

    const imageToDisplay = useMemo(() => {
        return displayRawImage ? image : thermalImageSrc;
    }, [displayRawImage, image, thermalImageSrc])


    useEffect(() => {
        if(fabricCanvas){
            const imageSrc = imageToDisplay;

            if(!imageSrc){
                return
            }

            fabric.Image.fromURL(imageSrc, function(img) {
                fabricCanvas.clear();
                const widthRatioChange = dimensions.width / img.width;
                const heightRatioChange = dimensions.height / img.height;
                let ratioChange;
                if(img.width*heightRatioChange > dimensions.width){
                    ratioChange = widthRatioChange;
                }else{
                    ratioChange = heightRatioChange
                }

                const newWidth = img.width*ratioChange;
                const newHeight = img.height*ratioChange;

                setCanvasDimensions([newHeight, newWidth]);
                fabricCanvas.setWidth(newWidth);
                fabricCanvas.setHeight(newHeight);

                const imageData = {
                    left: 0,
                    top: 0,
                    controls: {},
                    hasBorders: false,
                    selectable: false,
                    scaleY: ratioChange,
                    scaleX: ratioChange,
                }

                const oImg = img.set(imageData);
                setImgObj(oImg);
                fabricCanvas.add(oImg);
                thermalImage.current = null;
                setLoading(false)
            }, {crossOrigin: 'anonymous'});

            fabricCanvas.renderAll();
        }
    }, [dimensions.height, dimensions.width, displayRawImage, fabricCanvas, imageToDisplay]);

    useEffect(() => {
        if(absoluteImageCanvas){
            const imageElem = document.createElement('img');
            const ctx = absoluteImageCanvas.getContext("2d");
            imageElem.src = image;
            imageElem.onload = function(){
                ctx.drawImage(imageElem, 0, 0, canvasDimensions[1], canvasDimensions[0])
            }
        }

        return () => {
            const imageElem = document.createElement('img');
            imageElem.src = null;
        }
    }, [absoluteImageCanvas, canvasDimensions, image])

    return (
        <>
            <Measure
                bounds
                onResize={contentRect => {
                    setDimensions(contentRect.bounds);
                }}>
                {({ measureRef }) => {
                    return (
                        <>
                            {!absoluteImageLoading && <div className="TempDisplayContainer">
                                <span>Temperature: {_.isNil(hoveredTemp) ? "N/A" : hoveredTemp }</span>
                            </div>}
                            {absoluteImageLoading && <div className="AbsoluteImageSpinnerContainer" style={{position: "absolute", zIndex: 2000}}>
                                <OBCSpinner
                                    size={80}
                                    speed={3}
                                    colorScheme={"mono"}
                                />
                            </div>}
                            <div
                                className="AbsoluteTempOverlay"
                                ref={measureRef}>
                                <canvas
                                    ref={c => setFabricCanvasRef(c)}
                                    className="ZoomFabricCanvas"
                                />
                                <canvas
                                    height={canvasDimensions[0]}
                                    width={canvasDimensions[1]}
                                    ref={c => setAbsoluteImageCanvas(c)}
                                    className={"AbsoluteImageCanvas" + (displayRawImage ? "" : " Hidden")}/>
                            </div>
                        </>
                    )
                }}
            </Measure>
        </>
    )
}

export default AbsoluteColourImageDisplay;